import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  ExternalLink,
  Users,
  FileText,
  Loader,
  CircleDot
} from 'lucide-react';

const ContractBillingDue = ({ 
  customers, 
  onGenerateInvoice, 
  generatingFor,
  onViewJobs 
}) => {
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [expandedPeriods, setExpandedPeriods] = useState(new Set());

  const toggleCustomer = (customerId) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
    }
    setExpandedCustomers(newExpanded);
  };

  const togglePeriod = (key) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedPeriods(newExpanded);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getBillingTypeColor = (type) => {
    switch (type) {
      case 'Weekly': return '';
      case 'Monthly': return '';
      case 'Quarterly': return '';
      default: return '';
    }
  };

  const getBillingTypeStyle = (type) => {
    switch (type) {
      case 'Weekly': return {background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#93c5fd'};
      case 'Monthly': return {background: 'linear-gradient(135deg, #1e1b4b, #312e81)', color: '#a5b4fc'};
      case 'Quarterly': return {background: 'linear-gradient(135deg, #422006, #78350f)', color: '#fcd34d'};
      default: return {background: 'linear-gradient(135deg, #1e293b, #334155)', color: '#94a3b8'};
    }
  };

  const getBillingPeriodShort = (type) => {
    switch (type) {
      case 'Weekly': return 'wk';
      case 'Monthly': return 'mo';
      case 'Quarterly': return 'qtr';
      default: return '';
    }
  };

  const getStatusSummary = (customer) => {
    if (customer.previousPeriod?.isReady) {
      return {
        text: `${customer.previousPeriod.label} Ready`,
        className: 'text-green-600 font-medium'
      };
    }
    if (customer.currentPeriod?.jobCount > 0) {
      return {
        text: `${customer.currentPeriod.label} - ${customer.currentPeriod.jobCount} job${customer.currentPeriod.jobCount > 1 ? 's' : ''}`,
        className: 'text-gray-600'
      };
    }
    return {
      text: 'No activity',
      className: 'text-gray-400'
    };
  };

  if (!customers || customers.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Contract Customers</h3>
        <p className="text-gray-500 text-sm">
          Customers with weekly, monthly, or quarterly billing will appear here.
        </p>
      </div>
    );
  }

  const readyCount = customers.filter(c => c.previousPeriod?.isReady).length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700" style={{background: 'linear-gradient(135deg, #1e1b4b, #312e81)'}}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-900/50 rounded-lg">
            <Users size={20} className="text-indigo-300" />
          </div>
          <div>
            <h3 className="font-semibold text-indigo-100">Contract Billing</h3>
            <p className="text-sm text-indigo-300">
              {customers.length} customer{customers.length !== 1 ? 's' : ''}
              {readyCount > 0 && (
                <span className="ml-1">• <span className="text-green-400 font-medium">{readyCount} ready to invoice</span></span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Customer List - Table style on desktop, cards on mobile */}
      <div className="divide-y divide-gray-100">
        {customers.map((customer) => {
          const isExpanded = expandedCustomers.has(customer.id);
          const isReady = customer.previousPeriod?.isReady;
          const prevPeriodKey = `prev-${customer.id}`;
          const currentPeriodKey = `current-${customer.id}`;
          const isPrevExpanded = expandedPeriods.has(prevPeriodKey);
          const isCurrentExpanded = expandedPeriods.has(currentPeriodKey);
          const isGenerating = generatingFor === customer.id;
          const status = getStatusSummary(customer);

          return (
            <div key={customer.id}>
              {/* Collapsed Row - Click to expand */}
              <button
                onClick={() => toggleCustomer(customer.id)}
                className={`w-full text-left transition-colors ${
                  isReady 
                    ? 'hover:opacity-95 border-l-4 border-l-green-400' 
                    : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                }`}
              >
                {/* Desktop Row */}
                <div className="hidden sm:flex items-center px-4 py-3 gap-4">
                  {/* Ready indicator */}
                  <div className="w-6 flex-shrink-0">
                    {isReady && (
                      <CircleDot size={18} className="text-green-500" />
                    )}
                  </div>

                  {/* Customer Name */}
                  <div className="w-40 flex-shrink-0">
                    <span className="font-medium text-gray-900">{customer.name}</span>
                  </div>

                  {/* Billing Type */}
                  <div className="w-24 flex-shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={getBillingTypeStyle(customer.billingType)}>
                      {customer.billingType}
                    </span>
                  </div>

                  {/* Billing Day */}
                  <div className="w-20 flex-shrink-0 text-sm text-gray-500">
                    Day {customer.billingDay}
                  </div>

                  {/* Rate */}
                  <div className="w-28 flex-shrink-0 text-sm">
                    {customer.contractRate ? (
                      <span className="text-green-600 font-medium">
                        {formatCurrency(customer.contractRate)}/{getBillingPeriodShort(customer.billingType)}
                      </span>
                    ) : (
                      <span className="text-gray-400">Sum jobs</span>
                    )}
                  </div>

                  {/* Status */}
                  <div className={`flex-1 text-sm ${status.className}`}>
                    {status.text}
                  </div>

                  {/* Expand icon */}
                  <div className="w-8 flex-shrink-0 text-gray-400">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                </div>

                {/* Mobile Row */}
                <div className="sm:hidden px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isReady && <CircleDot size={16} className="text-green-500" />}
                      <span className="font-medium text-gray-900">{customer.name}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-sm">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={getBillingTypeStyle(customer.billingType)}>
                      {customer.billingType}
                    </span>
                    {customer.contractRate && (
                      <span className="text-green-600 font-medium">
                        {formatCurrency(customer.contractRate)}/{getBillingPeriodShort(customer.billingType)}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${status.className}`}>{status.text}</p>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 bg-gray-50">
                  <div className="pt-2 space-y-3">
                    {/* Previous Period - Ready to Invoice */}
                    {customer.previousPeriod?.isReady && (
                      <div className="bg-white border border-green-200 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-green-600" />
                            <span className="font-medium text-green-800">READY TO INVOICE</span>
                          </div>
                          <span className="text-green-700 font-semibold">{customer.previousPeriod.label}</span>
                        </div>

                        {/* Job summary - collapsible */}
                        {customer.previousPeriod.jobCount > 0 ? (
                          <div className="mb-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); togglePeriod(prevPeriodKey); }}
                              className="flex items-center gap-1 text-sm text-green-700 hover:text-green-800"
                            >
                              {isPrevExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              <span>{customer.previousPeriod.jobCount} job{customer.previousPeriod.jobCount > 1 ? 's' : ''} completed</span>
                              <span className="font-semibold ml-1">• {formatCurrency(customer.previousPeriod.jobsTotal)}</span>
                            </button>

                            {isPrevExpanded && (
                              <div className="mt-2 ml-5 space-y-1">
                                {customer.previousPeriod.jobs.map((job) => (
                                  <div key={job.id} className="text-sm text-green-700">
                                    {job.date} - {formatCurrency(job.amount)}
                                  </div>
                                ))}
                                {onViewJobs && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); onViewJobs(customer.id, customer.previousPeriod.periodStart, customer.previousPeriod.periodEnd); }}
                                    className="text-sm text-green-600 hover:text-green-800 flex items-center gap-1 mt-2"
                                  >
                                    <ExternalLink size={12} />
                                    View Jobs
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-green-700 mb-3">
                            No jobs this period • Fixed contract rate: {formatCurrency(customer.contractRate)}
                          </p>
                        )}

                        {/* Generate Invoice Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); onGenerateInvoice(customer.id, customer.previousPeriod.periodStart, customer.previousPeriod.periodEnd); }}
                          disabled={isGenerating}
                          className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isGenerating ? (
                            <>
                              <Loader size={16} className="animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <FileText size={16} />
                              Generate {customer.previousPeriod.label} Invoice
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Current Period - In Progress */}
                    {customer.currentPeriod && (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Clock size={16} className="text-gray-500" />
                            <span className="font-medium text-gray-600">CURRENT PERIOD</span>
                          </div>
                          <span className="text-gray-700 font-semibold">{customer.currentPeriod.label}</span>
                        </div>

                        {customer.currentPeriod.jobCount > 0 ? (
                          <div>
                            <button
                              onClick={(e) => { e.stopPropagation(); togglePeriod(currentPeriodKey); }}
                              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                            >
                              {isCurrentExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              <span>{customer.currentPeriod.jobCount} job{customer.currentPeriod.jobCount > 1 ? 's' : ''} completed</span>
                              <span className="font-semibold ml-1">• {formatCurrency(customer.currentPeriod.jobsTotal)}</span>
                            </button>

                            {isCurrentExpanded && (
                              <div className="mt-2 ml-5 space-y-1">
                                {customer.currentPeriod.jobs.map((job) => (
                                  <div key={job.id} className="text-sm text-gray-600">
                                    {job.date} - {formatCurrency(job.amount)}
                                  </div>
                                ))}
                                {onViewJobs && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); onViewJobs(customer.id, customer.currentPeriod.periodStart, customer.currentPeriod.periodEnd); }}
                                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mt-2"
                                  >
                                    <ExternalLink size={12} />
                                    View Jobs
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No jobs completed yet</p>
                        )}

                        {/* Invoiceable after notice */}
                        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                          <span>💡</span>
                          Invoiceable after {customer.currentPeriod.invoiceableAfterLabel}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContractBillingDue;